import React from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';            // default export
import { Notice } from '../patterns';
import {
  normalizeMeasurementType, showsSamplingAndTally,
  SPEC_TYPES, SAMPLING_UNITS, TALLY_THRESHOLDS, CATEGORIES, TYPE_COLOR_KEY,
} from '../../scouting/pestConfig';
import { usePestConfigEditor } from '../../scouting/usePestConfigEditor';
import { PestFieldPreview } from './PestFieldPreview';
import './PestConfigPage.css';

/** Label-left / control-right editor row (matches the desktop config mockup). */
const Row: React.FC<{ label: string; help?: string; children: React.ReactNode }> = ({ label, help, children }) => (
  <div className="pcfg-row">
    <div className="pcfg-row__label">
      {label}
      {help && <div className="pcfg-row__help">{help}</div>}
    </div>
    <div className="pcfg-row__control">{children}</div>
  </div>
);

const PestConfigPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    pests, selectedId, selected, draft, descriptor: desc, error, success, saving,
    select, changeType, patchDraft, adjustThreshold, resetDraft, save,
  } = usePestConfigEditor();

  return (
    <div className="pest-config-page">
      <PageHeader
        title={t('fieldScout.config.title')}
        meta={t('fieldScout.config.subtitle', { count: pests.length })}
      />
      {error && <Notice tone="danger" className="mb-3"><span>{error}</span></Notice>}
      {success && <Notice tone="success" role="alert" className="mb-3"><span>{success}</span></Notice>}

      <div className="pest-config-page__body">
        <div className="pest-config-page__list" role="list" aria-label={t('fieldScout.config.listLabel')}>
          {pests.map(p => {
            const spec = normalizeMeasurementType(p.measurementType);
            return (
              <button
                key={p._id}
                type="button"
                role="listitem"
                aria-current={p._id === selectedId ? 'true' : undefined}
                className={`pest-config-row${p._id === selectedId ? ' is-selected' : ''}`}
                onClick={() => select(p)}
              >
                <span className="pest-config-row__code">{p.code}</span>
                <span className="pest-config-row__main">
                  <span className="pest-config-row__name">{p.name}</span>
                  <span className={`pest-config-row__type k-${TYPE_COLOR_KEY[spec]}`}>
                    {t(`fieldScout.config.type.${spec}`)}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {draft && desc && selected && (
          <div className="pest-config-page__editor">
            <div className="pcfg-head">
              <span className="pcfg-head__code">{selected.code}</span>
              <span className="pcfg-head__name">{selected.name}</span>
              <span className={`pest-config-row__type k-${TYPE_COLOR_KEY[draft.measurementType]}`}>
                {t(`fieldScout.config.type.${draft.measurementType}`)}
              </span>
            </div>
            <div className="pcfg-divider" />

            <div className="pcfg-editor">
              <Row label={t('fieldScout.config.measurementType')}>
                <div className="pest-config-seg" role="group" aria-label={t('fieldScout.config.measurementType')}>
                  {SPEC_TYPES.map(ty => (
                    <button
                      key={ty}
                      type="button"
                      aria-pressed={draft.measurementType === ty}
                      className={`pest-config-seg__btn k-${TYPE_COLOR_KEY[ty]}${draft.measurementType === ty ? ' is-active' : ''}`}
                      onClick={() => changeType(ty)}
                    >
                      {t(`fieldScout.config.type.${ty}`)}
                    </button>
                  ))}
                </div>
              </Row>

              {showsSamplingAndTally(draft.measurementType) ? (
                <>
                  <Row label={t('fieldScout.config.samplingUnit')}>
                    <div className="pest-config-chips" data-testid="field-sampling-unit">
                      {SAMPLING_UNITS.map(u => (
                        <button key={u} type="button" aria-pressed={draft.samplingUnit === u}
                          className={`pest-config-chip${draft.samplingUnit === u ? ' is-active' : ''}`}
                          onClick={() => patchDraft({ samplingUnit: u })}>
                          {t(`fieldScout.config.sampling.${u}`)}
                        </button>
                      ))}
                    </div>
                  </Row>
                  <Row label={t('fieldScout.config.tallyThreshold')} help={t('fieldScout.config.tallyHelp')}>
                    <div className="pest-config-chips" data-testid="field-tally-threshold">
                      {TALLY_THRESHOLDS.map(tt => (
                        <button key={tt} type="button" aria-pressed={draft.tallyThreshold === tt}
                          className={`pest-config-chip${draft.tallyThreshold === tt ? ' is-active' : ''}`}
                          onClick={() => patchDraft({ tallyThreshold: tt })}>
                          {t(`fieldScout.config.tally.${tt}`)}
                        </button>
                      ))}
                    </div>
                  </Row>
                </>
              ) : (
                <Notice tone="info" className="pest-config-note">
                  <span>{t('fieldScout.config.nonIncidenceNote', { type: t(`fieldScout.config.type.${draft.measurementType}`) })}</span>
                </Notice>
              )}

              <Row label={t('fieldScout.config.actionThreshold')}>
                <div className="pcfg-threshold">
                  <div className="pcfg-stepper" role="group" aria-label={t('fieldScout.config.actionThreshold')}>
                    <button type="button" aria-label={t('fieldScout.config.decrease')}
                      disabled={draft.actionThreshold <= desc.min}
                      onClick={() => adjustThreshold(-desc.step)}>−</button>
                    <div className="pcfg-stepper__v" data-testid="action-threshold-value">
                      {desc.render(draft.actionThreshold)}
                    </div>
                    <button type="button" aria-label={t('fieldScout.config.increase')}
                      disabled={draft.actionThreshold >= desc.max}
                      onClick={() => adjustThreshold(desc.step)}>+</button>
                  </div>
                  <span className="pcfg-hint">{t(desc.unitLabelKey)}</span>
                </div>
              </Row>

              <Row label={t('fieldScout.config.categoryLabel')}>
                <div className="pest-config-chips">
                  {CATEGORIES.map(c => (
                    <button key={c} type="button" aria-pressed={draft.category === c}
                      className={`pest-config-chip${draft.category === c ? ' is-active' : ''}`}
                      onClick={() => patchDraft({ category: c })}>
                      {t(`fieldScout.config.category.${c}`)}
                    </button>
                  ))}
                </div>
              </Row>

              {/* Live field preview — Slice 2b (Desktop Spec §C row 6) */}
              <Row label={t('fieldScout.config.fieldPreview')} help={t('fieldScout.config.fieldPreviewHelp')}>
                <PestFieldPreview
                  code={selected.code}
                  name={selected.name}
                  measurementType={draft.measurementType}
                  actionThreshold={draft.actionThreshold}
                />
              </Row>
            </div>

            <div className="pcfg-divider" />
            <div className="pest-config-actions">
              <button type="button" className="pat-btn pat-btn--subtle" disabled={saving}
                onClick={resetDraft}>
                {t('fieldScout.config.cancel')}
              </button>
              <button type="button" className="pat-btn pat-btn--primary" disabled={saving} onClick={save}>
                {t('fieldScout.config.savePest')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PestConfigPage;
